<script>
import { displayToast, handleResponseError, shouldShowTooltip } from '@/components/utils/globalFunction'
import ResponseConstant from '@/constants/responseConstant'
import ExpressistTransporterService from '@/services/transporters/ExpressistTransporterService'
import Validator from '@/components/utils/validator'

export default {
  name: 'ExpressistConnexionCreatePage',
  data() {
    return {
      loading: false,
      formCreateExpressistConnexionValid: false,

      currentStoreId: null,

      transporterList: [],
      selectedTransporter: null,

      connexionServiceList: [],
      selectedConnexionServiceList: [],

      connexionIdentifier: null,
      connexionPassword: null,
      showConnexionPassword: false,

      priorityStatus: false,

      requiredRules: Validator.requiredRules,
      max50CharRules: Validator.max50CharRules,
      max100CharRules: Validator.max100CharRules,

      error: {
        errorTransporterList: null,
        errorConnexionServiceList: null,
        errorConnexionIdentifier: null,
        errorConnexionPassword: null,
        errorPriorityStatus: null
      }
    }
  },
  async created() {
    this.currentStoreId = this.$route.params.storeId
    await this.getAllExpressistTransporterType()
  },
  watch: {
    async selectedTransporter() {
      if (this.selectedTransporter) {
        const selectedTransporterObject = this.transporterList.find(
          (transporter) => transporter.technical_id === this.selectedTransporter
        )
        await this.getAllExpressistTransporterService(selectedTransporterObject.transporter_type)
      } else {
        this.connexionServiceList = []
        this.selectedConnexionServiceList = []
      }
    }
  },
  methods: {
    shouldShowTooltip,
    async getAllExpressistTransporterType() {
      try {
        const response = await ExpressistTransporterService.getAllTransporterList()
        this.transporterList = response.item_list
      } catch (error) {
        handleResponseError(error)
      }
    },

    async getAllExpressistTransporterService(selectedType) {
      try {
        if (selectedType != null) {
          const response =
            await ExpressistTransporterService.getAllExpressistTransporterServiceByType(
              selectedType
            )
          this.connexionServiceList = response.item_list
        } else {
          displayToast(this.$t('EXPRESSIST_TRANSPORTER.MESSAGE.ERROR_TYPE_IS_MANDATORY'), 'warning')
        }
      } catch (error) {
        handleResponseError(error)
      }
    },

    async submit() {
      this.loading = true

      let data = {
        transporter_id: this.selectedTransporter,
        connexion_service_list: this.selectedConnexionServiceList,
        connexion_identifier: this.connexionIdentifier,
        connexion_password: this.connexionPassword,
        link_priority: this.priorityStatus,
        store_id: this.currentStoreId
      }

      try {
        await ExpressistTransporterService.createExpressistTransporterForStore(data)

        displayToast(this.$t('EXPRESSIST_TRANSPORTER.CREATE_PAGE.MESSAGE.SUCCESS'), 'success')
        this.$router.push('/dashboard/transporters')
      } catch (error) {
        handleResponseError(error)
        if (error.response?.data && error.response.data.key in ResponseConstant) {
          this.setError(error.response.data.key)
        }
      } finally {
        this.loading = false
      }
    },
    togglePasswordVisibility() {
      this.showConnexionPassword = !this.showConnexionPassword
    },
    setError(key) {
      switch (key) {
        case 'expressist_distributor_transporter_not_found_error_key':
          this.error = { errorTransporterList: this.$t('ERROR.' + ResponseConstant[key]) }
          break
        case 'expressist_transporter_connexion_invalid_login_error_key':
          this.error = { errorConnexionIdentifier: this.$t('ERROR.' + ResponseConstant[key]) }
          break
        case 'expressist_transporter_connexion_invalid_password_error_key':
          this.error = { errorConnexionPassword: this.$t('ERROR.' + ResponseConstant[key]) }
          break
        case 'expressist_transporter_connexion_invalid_service_error_key':
          this.error = { errorConnexionServiceList: this.$t('ERROR.' + ResponseConstant[key]) }
          break
        case 'expressist_transporter_link_priority_error_key':
          this.error = { errorPriorityStatus: this.$t('ERROR.' + ResponseConstant[key]) }
          break
        default:
          this.error = ''
      }
    }
  }
}
</script>

<template>
  <v-container fluid>
    <!--HEADER-->
    <div class="d-flex flex-column flex-md-row justify-space-between">
      <div>
        <h1>{{ $t('EXPRESSIST_TRANSPORTER.CREATE_PAGE.TITLE.HEADER') }}</h1>
        <span>{{ $t('EXPRESSIST_TRANSPORTER.CREATE_PAGE.TITLE.SUBTITLE') }}</span>
      </div>
    </div>

    <v-form
      ref="formCreateExpressistConnexion"
      v-model="formCreateExpressistConnexionValid"
      @submit.prevent="submit"
    >
      <v-row>
        <v-col cols="12">
          <v-card class="rounded-xl mt-4" :border="false" :elevation="0">
            <!--Form-->
            <v-row>
              <!--Transporter Type-->
              <v-col cols="12" md="6">
                <p data-custom="true">
                  {{ $t('EXPRESSIST_TRANSPORTER.FORM.LABEL.TRANSPORTER_TYPE') }}
                </p>
                <v-autocomplete
                  id="select-transporter-type"
                  v-model="selectedTransporter"
                  :items="transporterList"
                  :no-data-text="$t('COMMON.NO_DATA')"
                  :rules="requiredRules"
                  item-title="name"
                  item-value="technical_id"
                  required
                  :error-messages="error?.errorTransporterList"
                  @update:modelValue="error = null"
                  clearable
                >
                  <template v-slot:selection="{ item }">
                    <v-chip size="small">
                      <span>{{ item.title }}</span>
                    </v-chip>
                  </template>
                  <template v-slot:item="{ item, props }">
                    <v-list-item :title="item.title" :value="item.value" v-bind="props">
                      <v-tooltip
                        v-if="shouldShowTooltip(item, 'select-transporter-type')"
                        activator="parent"
                        location="bottom left"
                      >
                        {{ item.title }}
                      </v-tooltip>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>

              <!--Connexion Service List-->
              <v-col cols="12" md="6">
                <p data-custom="true">
                  {{ $t('EXPRESSIST_TRANSPORTER.FORM.LABEL.CONNEXION_SERVICE') }}
                </p>
                <v-autocomplete
                  id="select-connexion-service"
                  v-model="selectedConnexionServiceList"
                  :items="connexionServiceList"
                  :no-data-text="$t('COMMON.NO_DATA')"
                  :rules="requiredRules"
                  item-title="name"
                  item-value="code"
                  required
                  :error-messages="error?.errorConnexionServiceList"
                  @update:modelValue="error = null"
                  multiple
                  clearable
                  :disabled="this.selectedTransporter === null"
                >
                  <template v-slot:selection="{ item }">
                    <v-chip size="small">
                      <span>{{ item.title }}</span>
                    </v-chip>
                  </template>
                  <template v-slot:item="{ item, props }">
                    <v-list-item :title="item.title" :value="item.value" v-bind="props">
                      <v-tooltip
                        v-if="shouldShowTooltip(item, 'select-connexion-service')"
                        activator="parent"
                        location="bottom left"
                      >
                        {{ item.title }}
                      </v-tooltip>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <v-row>
              <!--Connexion Identifier-->
              <v-col cols="12" md="6">
                <p data-custom="true">
                  {{ $t('EXPRESSIST_TRANSPORTER.FORM.LABEL.CONNEXION_IDENTIFIER') }}
                </p>
                <v-text-field
                  v-model="connexionIdentifier"
                  :rules="[...requiredRules, ...max100CharRules]"
                  required
                  counter="100"
                  maxlength="100"
                  v-on:input="error = null"
                  :error-messages="error?.errorConnexionIdentifier"
                />
              </v-col>

              <!--Connexion password-->
              <v-col cols="12" md="6">
                <p data-custom="true">
                  {{ $t('EXPRESSIST_TRANSPORTER.FORM.LABEL.CONNEXION_PASSWORD') }}
                </p>
                <v-text-field
                  :type="showConnexionPassword ? 'text' : 'password'"
                  v-model="connexionPassword"
                  :rules="[...requiredRules, ...max50CharRules]"
                  required
                  counter="50"
                  maxlength="50"
                  v-on:input="error = null"
                  :error-messages="error?.errorConnexionPassword"
                  @click:append-inner="togglePasswordVisibility"
                  :append-inner-icon="showConnexionPassword ? 'mdi-eye' : 'mdi-eye-off'"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <label>{{ $t('EXPRESSIST_TRANSPORTER.FORM.LABEL.LINK_PRIORITY') }}</label>
                <v-switch v-model="priorityStatus" class="v-switch" color="#559a1b" inset />
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <!--Validation and Back Button-->
      <v-row>
        <v-col cols="12" class="d-flex justify-start">
          <v-btn
            :disabled="!formCreateExpressistConnexionValid || loading"
            :loading="loading"
            class="boom-button mr-4"
            type="submit"
            variant="outlined"
          >
            {{ $t('EXPRESSIST_TRANSPORTER.CREATE_PAGE.BUTTON.CREATE_TRANSPORTER') }}
          </v-btn>

          <v-btn :to="'/dashboard/transporters'" class="boom-button-back" variant="outlined">
            {{ $t('COMMON.BUTTON.BACK') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<style scoped>
.v-container {
  padding: 32px;
}

.v-card {
  padding: 32px;
}

.v-form p {
  margin-bottom: 16px;
}
</style>
